<template>
  <div class="accounts-browse">
    <div
      class="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom"
    >
      <h1 class="h2">Accounts</h1>
      <span v-if="total >= 0" class="text-muted">{{
        total | pluralize("account")
      }}</span>
    </div>

    <div class="form-row mb-3">
      <div class="col-md-4 mb-2">
        <input
          class="form-control"
          type="text"
          placeholder="Search by email…"
          v-model="filters.search"
          v-on:input="onSearchInput"
        />
      </div>
      <div class="col-md-3 mb-2">
        <select
          class="form-control"
          v-model="filters.plan_id"
          v-on:change="applyFilters"
        >
          <option :value="null">Any plan</option>
          <option v-for="plan in plans" :key="plan.id" :value="plan.id">
            {{ plan.display }} ({{ plan.accounts }})
          </option>
        </select>
      </div>
      <div class="col-md-2 mb-2">
        <select
          class="form-control"
          v-model="filters.lock_reason"
          v-on:change="applyFilters"
        >
          <option :value="null">Any status</option>
          <option value="none">Active</option>
          <option value="any">Locked (any)</option>
          <option value="ban">Banned</option>
          <option value="email">Unverified email</option>
          <option value="bounced">Bounced</option>
          <option value="domains">Over domains</option>
        </select>
      </div>
      <div class="col-md-3 mb-2">
        <select
          class="form-control"
          v-model="filters.country"
          v-on:change="applyFilters"
        >
          <option :value="null">Any country</option>
          <option
            v-for="country in countries"
            :key="country.code"
            :value="country.code"
          >
            {{ country.name }}
          </option>
        </select>
      </div>
    </div>

    <template v-if="loading">
      <div class="alert alert-info" role="alert">Loading…</div>
    </template>
    <template v-else-if="total > 0">
      <div class="table-responsive">
        <table class="table table-striped table-hover">
          <thead>
            <tr>
              <td>Account</td>
              <td>Email</td>
              <td>Domains</td>
              <td>Country</td>
              <td>Created</td>
              <td class="text-center">Premium?</td>
              <td>Status</td>
            </tr>
          </thead>
          <tbody>
            <tr v-for="account in items" v-bind:key="account.email">
              <td>
                <router-link
                  :to="{
                    name: 'account-details',
                    params: { email: account.email },
                  }"
                >
                  {{ getName(account) | capitalize }}
                  <span v-if="account.locked">&#x1f512;</span>
                </router-link>
                <span v-if="account.plan" class="ml-2 badge badge-info">{{
                  account.plan.display
                }}</span>
              </td>
              <td>
                <router-link
                  :to="{
                    name: 'account-details',
                    params: { email: account.email },
                  }"
                  >{{ account.email }}</router-link
                >
              </td>
              <td>{{ account.domains }}</td>
              <td>{{ account.country || "—" }}</td>
              <td>{{ account.created | datetime }}</td>
              <td class="text-center">
                <i
                  v-if="account.premium"
                  class="icon ion-md-heart"
                  title="Premium"
                ></i>
                <i v-else class="icon ion-md-close-circle-outline"></i>
              </td>
              <td>
                <span
                  v-if="account.locked"
                  class="badge badge-danger"
                  :title="account.lock_reason"
                  >{{ account.lock_reason || "locked" }}</span
                >
                <span v-else class="badge badge-success">active</span>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="4">
                <a
                  v-if="offset > 0"
                  href="javascript:;"
                  title="View previous page"
                  v-on:click="previousPage"
                  class="btn btn-primary btn-sm"
                  >Previous</a
                >
              </td>
              <td colspan="3" class="text-right">
                <a
                  v-if="offset + limit < total"
                  href="javascript:;"
                  title="View next page"
                  v-on:click="nextPage"
                  class="btn btn-primary btn-sm"
                  >Next</a
                >
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </template>
    <template v-else>
      <div class="alert alert-warning" role="alert">
        No accounts match these filters.
      </div>
    </template>
  </div>
</template>

<script>
import { formatHttpError } from "../utils/errors";

export default {
  data() {
    return {
      total: -1,
      items: [],
      countries: [],
      plans: [],
      offset: 0,
      limit: 25,
      loading: false,
      searchTimeout: null,
      filters: {
        search: "",
        plan_id: null,
        lock_reason: null,
        country: null,
      },
    };
  },
  created() {
    this.fetch();
  },
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
  methods: {
    getName(account) {
      if (account.company_name) {
        return account.company_name;
      }
      return account.email
        .split("@")[0]
        .replace(".", " ")
        .replace("-", " ")
        .replace("_", " ")
        .replace("+", " ");
    },
    fetch() {
      this.loading = true;
      this.$http
        .get("accounts", {
          params: {
            search: this.filters.search,
            plan_id: this.filters.plan_id,
            lock_reason: this.filters.lock_reason,
            country: this.filters.country,
            offset: this.offset,
            limit: this.limit,
          },
        })
        .then(
          (response) => {
            this.total = response.body.total;
            this.items = response.body.items;
            this.limit = response.body.limit;
            this.countries = response.body.countries;
            this.plans = response.body.plans;
            this.loading = false;
          },
          (response) => {
            this.loading = false;
            if (response.status === 401) return;
            alert(formatHttpError(response));
          },
        );
    },
    applyFilters() {
      this.offset = 0;
      this.fetch();
    },
    onSearchInput() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(this.applyFilters, 300);
    },
    nextPage() {
      this.offset += this.limit;
      this.fetch();
    },
    previousPage() {
      this.offset -= this.limit;
      if (this.offset < 0) {
        this.offset = 0;
      }
      this.fetch();
    },
  },
};
</script>
